import React, { useEffect, useRef, useState } from 'react';
import { fetchAdminTeams, saveTeamConfig, deleteTeamConfig, fetchAdminLeagues, saveLeagueConfig, fetchTeamConfig, fetchTeamLeagueIndex, syncAllLeagueTeams } from '../services/api';

export default function Admin() {
  const [tab, setTab] = useState('leagues');

  return (
    <div>
      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'leagues' ? 'admin-tab--active' : ''}`} onClick={() => setTab('leagues')}>Leagues</button>
        <button className={`admin-tab ${tab === 'teams' ? 'admin-tab--active' : ''}`} onClick={() => setTab('teams')}>Teams</button>
        <button className={`admin-tab ${tab === 'users' ? 'admin-tab--active' : ''}`} onClick={() => setTab('users')}>Users</button>
        <button className={`admin-tab ${tab === 'activity' ? 'admin-tab--active' : ''}`} onClick={() => setTab('activity')}>Activity</button>
      </div>
      {tab === 'leagues' && <LeagueAdmin />}
      {tab === 'teams' && <TeamAdmin />}
      {tab === 'users' && <UsersAdmin />}
      {tab === 'activity' && <ActivityAdmin />}
    </div>
  );
}

// =====================
// LEAGUE ADMIN
// =====================
/**
 * "Last updated" line for a config form header. Renders nothing for configs
 * that predate the stamp, rather than claiming a date it does not have.
 */
function LastUpdated({ at, by }) {
  if (!at) return null;
  const when = new Date(at);
  if (isNaN(when)) return null;

  const days = Math.floor((Date.now() - when) / 86400000);
  const relative =
    days <= 0 ? 'today'
    : days === 1 ? 'yesterday'
    : days < 30 ? `${days} days ago`
    : when.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div
      style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}
      title={when.toLocaleString()}
    >
      Last updated {relative}{by ? ` by ${by}` : ''}
    </div>
  );
}

function LeagueAdmin() {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showAddLeague, setShowAddLeague] = useState(false);
  const [footyLookup, setFootyLookup] = useState([]);
  const [footyLoading, setFootyLoading] = useState(false);
  const [footySearch, setFootySearch] = useState('');
  const [addForm, setAddForm] = useState({ footyStatsName: '', seasonId: '', displayName: '', country: '', gender: 'Male', timezone: 'Europe/London' });
  const [adding, setAdding] = useState(false);
  const [addMsg, setAddMsg] = useState(null);

  useEffect(() => {
    fetchAdminLeagues().then(data => { setLeagues(data); setLoading(false); });
  }, []);

  async function openAddLeague() {
    setShowAddLeague(true);
    setFootySearch('');
    setAddMsg(null);
    setAddForm({ footyStatsName: '', seasonId: '', displayName: '', country: '', gender: 'Male', timezone: 'Europe/London' });
    if (footyLookup.length === 0) {
      setFootyLoading(true);
      try {
        const res = await fetch('/api/admin/footystats-lookup', { credentials: 'include' });
        const data = await res.json();
        setFootyLookup(data);
      } catch {
        setFootyLookup([]);
      } finally {
        setFootyLoading(false);
      }
    }
  }

  async function handleAddLeague(e) {
    e.preventDefault();
    setAdding(true);
    setAddMsg(null);
    try {
      const res = await fetch('/api/admin/leagues/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(addForm)
      });
      const data = await res.json();
      if (!res.ok) {
        setAddMsg({ type: 'error', text: data.error || 'Failed to create league' });
      } else {
        setAddMsg({ type: 'success', text: `Liga angelegt: ${data.configFile}` });
        // Reload league list
        fetchAdminLeagues().then(setLeagues);
        setTimeout(() => { setShowAddLeague(false); setAddMsg(null); }, 2000);
      }
    } catch {
      setAddMsg({ type: 'error', text: 'Connection error' });
    } finally {
      setAdding(false);
    }
  }

  function handleSelect(league) {
    setSelected(league);
    setForm(JSON.parse(JSON.stringify(league.config)));
    setSavedMsg(null);
  }

  function handleField(path, value) {
    setForm(prev => {
      const next = { ...prev };
      if (path.startsWith('getty.')) {
        const key = path.replace('getty.', '');
        next.getty = { ...prev.getty, [key]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setSavedMsg(null);
    try {
      await saveLeagueConfig(selected.configFile, form);
      setLeagues(prev => prev.map(l =>
        l.configFile === selected.configFile ? { ...l, config: form } : l
      ));
      setSavedMsg('Saved');
    } catch {
      setSavedMsg('Error saving');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete league "${selected.config.displayName}"?\n\nThe config file will be removed. Team configs are NOT deleted — they are shared across leagues.`)) return;
    try {
      const res = await fetch(`/api/admin/leagues/${selected.configFile}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) { setSavedMsg('Error: ' + (data.error || 'Delete failed')); return; }
      setLeagues(prev => prev.filter(l => l.configFile !== selected.configFile));
      setSelected(null);
      setForm(null);
    } catch {
      setSavedMsg('Connection error');
    }
  }

  async function handleSyncTeams() {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`/api/admin/leagues/${selected.configFile}/sync-teams`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) { setSyncResult({ error: data.error || 'Sync failed' }); return; }
      setSyncResult(data);
    } catch {
      setSyncResult({ error: 'Connection error' });
    } finally {
      setSyncing(false);
    }
  }

  // Group by country
  const grouped = {};
  leagues.forEach(l => {
    const country = l.config.country || 'Unknown';
    if (!grouped[country]) grouped[country] = [];
    grouped[country].push(l);
  });
  const groupedEntries = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));

  return (
    <>
    <div className="admin-layout">
      <div className="admin-sidebar">
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-title">League configs</div>
          <button className="btn btn--primary" style={{ fontSize: '12px', padding: '4px 10px' }} onClick={openAddLeague}>+ Add</button>
        </div>
        {loading ? (
          <p className="status-text" style={{ padding: '16px' }}>Loading...</p>
        ) : (
          <ul className="team-list">
            {groupedEntries.map(([country, countryLeagues]) => (
              <React.Fragment key={country}>
                <li className="team-list-country">{country}</li>
                {countryLeagues.map(league => (
                  <li
                    key={league.configFile}
                    className={`team-list-item ${selected?.configFile === league.configFile ? 'team-list-item--active' : ''}`}
                    onClick={() => handleSelect(league)}
                  >
                    <span className="team-list-name">{league.config.displayName}</span>
                    <span className="team-list-status" style={{ color: league.active ? 'var(--color-success)' : 'var(--color-grey-medium)' }}>
                      {league.active ? 'Active' : 'Inactive'}
                    </span>
                  </li>
                ))}
              </React.Fragment>
            ))}
          </ul>
        )}
      </div>

      <div className="admin-editor">
        {!selected ? (
          <div className="admin-empty">Select a league to edit</div>
        ) : (
          <>
            <div className="admin-form-header">
              <div className="admin-form-title">{selected.config.displayName}</div>
              <div className="admin-form-file">{selected.configFile}</div>
              <LastUpdated at={selected.config.lastUpdated} by={selected.config.lastUpdatedBy} />
            </div>

            <div className="form-section">
              <div className="form-section-title">Identity</div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Display name <span className="required">*</span></label>
                  <input value={form.displayName} onChange={e => handleField('displayName', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Venue name source</label>
                  <select value={form.venueNameSource} onChange={e => handleField('venueNameSource', e.target.value)}>
                    <option value="venueName">venueName (national)</option>
                    <option value="venueNameUEFA">venueNameUEFA (UEFA)</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Timezone <span className="required">*</span></label>
                  <input value={form.timezone || ''} onChange={e => handleField('timezone', e.target.value)} placeholder="e.g. Europe/Berlin" />
                </div>
                <div className="form-field">
                  <label>Current season</label>
                  <input value={form.currentSeason || ''} onChange={e => handleField('currentSeason', Number(e.target.value))} placeholder="e.g. 2025" type="number" />
                </div>
                <div className="form-field">
                  <label>Season format</label>
                  <select value={form.seasonFormat || ''} onChange={e => handleField('seasonFormat', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="start-year">start-year (NFL, MLB, MLS)</option>
                    <option value="end-year">end-year (NBA, NHL, Soccer)</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Season label</label>
                  <input value={form.seasonLabel || ''} onChange={e => handleField('seasonLabel', e.target.value)} placeholder="e.g. 2025/26" />
                </div>
                <div className="form-field">
                  <label>Include MD-1 activities (UEFA)</label>
                  <select value={form.includeMD1Activities === true ? 'true' : 'false'} onChange={e => handleField('includeMD1Activities', e.target.value === 'true')}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Include qualifying rounds</label>
                  <select value={form.includeQualifying === true ? 'true' : 'false'} onChange={e => handleField('includeQualifying', e.target.value === 'true')}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">Templates</div>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="form-field">
                  <label>Event name template</label>
                  <input value={form.eventNameTemplate} onChange={e => handleField('eventNameTemplate', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Caption template</label>
                  <input value={form.captionTemplate} onChange={e => handleField('captionTemplate', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">Getty fields</div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Source <span className="required">*</span></label>
                  <input value={form.getty?.source || ''} onChange={e => handleField('getty.source', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Classification 1 <span className="required">*</span></label>
                  <input value={form.getty?.classification1 || ''} onChange={e => handleField('getty.classification1', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Parent Event MEID</label>
                  <input value={form.getty?.parentEventMeid || ''} onChange={e => handleField('getty.parentEventMeid', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Inclusion routing <span className="required">*</span></label>
                  <input value={form.getty?.inclusionRouting || ''} onChange={e => handleField('getty.inclusionRouting', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Add to bulletin</label>
                  <select value={form.getty?.addToBulletin || ''} onChange={e => handleField('getty.addToBulletin', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Y">Y</option>
                    <option value="N">N</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Exclusion routing</label>
                  <input value={form.getty?.exclusionRouting || ''} onChange={e => handleField('getty.exclusionRouting', e.target.value)} />
                </div>
              </div>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr', marginTop: '14px' }}>
                <div className="form-field">
                  <label>Event restrictions</label>
                  <input value={form.getty?.eventRestrictions || ''} onChange={e => handleField('getty.eventRestrictions', e.target.value)} placeholder="e.g. Betting Companies - ALL:Hide" />
                </div>
                <div className="form-field">
                  <label>Event restrictions special instructions</label>
                  <input value={form.getty?.eventRestrictionsSpecialInstructions || ''} onChange={e => handleField('getty.eventRestrictionsSpecialInstructions', e.target.value)} />
                </div>
              </div>
            </div>

            {syncResult && (
              <div style={{ margin: '12px 0', padding: '10px 14px', borderRadius: 6, fontSize: 13, background: syncResult.error ? '#fef2f2' : '#f0fdf4', color: syncResult.error ? '#dc2626' : '#16a34a', border: `1px solid ${syncResult.error ? '#fecaca' : '#86efac'}` }}>
                {syncResult.error ? syncResult.error : `Sync complete: ${syncResult.created} new team${syncResult.created !== 1 ? 's' : ''} added, ${syncResult.existing} already existed (${syncResult.total} total in league)`}
              </div>
            )}

            <div className="admin-form-footer">
              <button className="btn btn--danger" onClick={handleDelete}>Delete league</button>
              <button className="btn btn--secondary" onClick={handleSyncTeams} disabled={syncing} style={{ marginLeft: 'auto' }}>
                {syncing ? 'Syncing...' : 'Sync teams →'}
              </button>
              {savedMsg && (
                <span className={`save-msg ${savedMsg === 'Saved' ? 'save-msg--ok' : 'save-msg--error'}`}>
                  {savedMsg}
                </span>
              )}
              <button className="btn btn--primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save config →'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>

    {showAddLeague && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}>
        <div style={{
          background: '#ffffff', borderRadius: '8px', padding: '24px',
          width: '600px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)', color: '#111111'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontWeight: 700, fontSize: '16px', color: '#111111' }}>Add league from FootyStats</div>
            <button onClick={() => setShowAddLeague(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>×</button>
          </div>

          {footyLoading ? (
            <p className="status-text">Loading FootyStats leagues...</p>
          ) : (
            <>
              <input
                placeholder="Search leagues..."
                value={footySearch}
                onChange={e => setFootySearch(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', marginBottom: '12px', boxSizing: 'border-box', background: '#ffffff', color: '#111111' }}
              />
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '16px', background: '#ffffff' }}>
                {footyLookup
                  .filter(l => !footySearch || l.name.toLowerCase().includes(footySearch.toLowerCase()) || l.country.toLowerCase().includes(footySearch.toLowerCase()))
                  .map(l => (
                    <div
                      key={l.seasonId}
                      onClick={() => setAddForm(f => ({ ...f, footyStatsName: l.name, seasonId: l.seasonId, country: l.country, displayName: f.displayName || l.name.replace(/^[A-Za-z]+ /, '') }))}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                        background: addForm.footyStatsName === l.name ? '#ede9fe' : '#ffffff',
                        borderBottom: '1px solid #e5e7eb', color: '#111111'
                      }}
                    >
                      <strong>{l.name}</strong>
                      <span style={{ marginLeft: '8px', color: '#6b7280', fontSize: '12px' }}>{l.country} · Season ID: {l.seasonId}</span>
                    </div>
                  ))
                }
              </div>

              {addForm.footyStatsName && (
                <form onSubmit={handleAddLeague}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>FootyStats Name</label>
                      <input value={addForm.footyStatsName} readOnly style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', background: 'var(--color-bg)', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Season ID</label>
                      <input value={addForm.seasonId} readOnly style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', background: 'var(--color-bg)', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Display Name *</label>
                      <input value={addForm.displayName} onChange={e => setAddForm(f => ({ ...f, displayName: e.target.value }))} required style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Country *</label>
                      <input value={addForm.country} onChange={e => setAddForm(f => ({ ...f, country: e.target.value }))} required style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Gender</label>
                      <select value={addForm.gender} onChange={e => setAddForm(f => ({ ...f, gender: e.target.value }))} style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Timezone</label>
                      <input value={addForm.timezone} onChange={e => setAddForm(f => ({ ...f, timezone: e.target.value }))} style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                    </div>
                  </div>

                  {addMsg && (
                    <div style={{ marginBottom: '12px', padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                      background: addMsg.type === 'error' ? '#fef2f2' : '#f0fdf4',
                      color: addMsg.type === 'error' ? '#dc2626' : '#16a34a',
                      border: `1px solid ${addMsg.type === 'error' ? '#fecaca' : '#86efac'}`
                    }}>
                      {addMsg.text}
                    </div>
                  )}

                  <button type="submit" disabled={adding} className="btn btn--primary" style={{ width: '100%', justifyContent: 'center' }}>
                    {adding ? 'Creating...' : 'Create league config →'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
}

// =====================
// TEAM ADMIN
// =====================
function TeamAdmin() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [groupBy, setGroupBy] = useState('country');
  const [leagueIndex, setLeagueIndex] = useState(null);
  // Which league groups are collapsed out of view. Per-viewer convenience only,
  // so localStorage is the right home for it — wrapped because private windows
  // and blocked site data make both reads and writes throw.
  const [hiddenLeagues, setHiddenLeagues] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('teamAdmin.hiddenLeagues') || '[]'));
    } catch {
      return new Set();
    }
  });
  const [leaguePanelOpen, setLeaguePanelOpen] = useState(true);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncAllResult, setSyncAllResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAdminTeams()
      .then(data => { setTeams(data); setLoading(false); })
      .catch(() => { setSavedMsg('Error loading teams'); setLoading(false); });
    fetchTeamLeagueIndex().then(setLeagueIndex).catch(() => setLeagueIndex({}));
  }, []);

  async function handleSyncAll() {
    setSyncingAll(true);
    setSyncAllResult(null);
    try {
      const result = await syncAllLeagueTeams();
      setSyncAllResult(result);
      const [freshTeams, freshIndex] = await Promise.all([
        fetchAdminTeams(), fetchTeamLeagueIndex()
      ]);
      setTeams(freshTeams);
      setLeagueIndex(freshIndex);
    } catch {
      setSyncAllResult({ error: 'Sync failed' });
    } finally {
      setSyncingAll(false);
    }
  }

  async function handleSelect(team) {
    setSelected(team);
    setForm(null);
    setSavedMsg(null);
    try {
      const config = await fetchTeamConfig(team.filename);
      setForm(config);
      setSelected({ ...team, config });
    } catch (e) {
      setSavedMsg('Error loading config');
    }
  }

  function handleField(path, value) {
    setForm(prev => {
      const next = { ...prev };
      if (path.startsWith('venue.')) {
        const key = path.replace('venue.', '');
        next.venue = { ...prev.venue, [key]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setSavedMsg(null);
    try {
      await saveTeamConfig(selected.filename, form);
      setTeams(prev => prev.map(t =>
        t.filename === selected.filename
          ? {
              ...t,
              config: form,
              complete: isComplete(form),
              missingFields: getMissing(form),
              // keep the list row in step with the form, or the flag would not
              // show until the next reload
              name: form.name || t.name,
              country: form.country || '',
              gender: form.gender || '',
              needsAttention: form.needsAttention === true,
              attentionNote: form.attentionNote || '',
            }
          : t
      ));
      setSavedMsg('Saved');
    } catch {
      setSavedMsg('Error saving');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete config for ${selected.name}? This cannot be undone.`)) return;
    try {
      await deleteTeamConfig(selected.filename);
      setTeams(prev => prev.filter(t => t.filename !== selected.filename));
      setSelected(null);
      setForm(null);
    } catch {
      alert('Failed to delete config');
    }
  }

  async function handleBulkExport() {
    const res = await fetch('/api/admin/teams/bulk-export');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'team-configs-bulk.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleBulkImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch('/api/admin/teams/bulk-import', { method: 'POST', body: formData });
      const result = await res.json();
      setImportResult(result);
      const updated = await fetchAdminTeams();
      setTeams(updated);
      setSelected(null);
      setForm(null);
    } catch {
      setImportResult({ error: 'Import failed' });
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  }

  function isComplete(config) {
    return !!(config.gender && config.country);
  }

  function getMissing(config) {
    const missing = [];
    if (!config.gender) missing.push('gender');
    if (!config.country) missing.push('country');
    return missing;
  }

  const filtered = teams.filter(t => {
    if (filter === 'incomplete' && t.complete) return false;
    if (filter === 'complete' && !t.complete) return false;
    if (filter === 'attention' && !t.needsAttention) return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group headings sort alphabetically, except catch-all buckets which always
  // sink to the bottom regardless of name.
  const CATCH_ALL = ['Unknown', 'Not in a synced league', 'Unspecified'];
  function sortGroups([a], [b]) {
    const aCatch = CATCH_ALL.includes(a);
    const bCatch = CATCH_ALL.includes(b);
    if (aCatch !== bCatch) return aCatch ? 1 : -1;
    return a.localeCompare(b);
  }

  function buildGroups() {
    const grouped = {};
    const push = (key, team) => {
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(team);
    };

    if (groupBy === 'league') {
      // A club plays in several competitions, so it appears under each one.
      const byTeamId = new Map();
      Object.values(leagueIndex || {}).forEach(entry => {
        (entry.teamIds || []).forEach(id => {
          if (!byTeamId.has(id)) byTeamId.set(id, []);
          byTeamId.get(id).push(entry.displayName);
        });
      });
      filtered.forEach(team => {
        const leagues = byTeamId.get(team.teamId);
        if (leagues && leagues.length) leagues.forEach(l => push(l, team));
        else push('Not in a synced league', team);
      });
    } else if (groupBy === 'status') {
      filtered.forEach(team => push(team.complete ? 'Complete' : 'Incomplete', team));
    } else if (groupBy === 'gender') {
      filtered.forEach(team => push(team.gender || 'Unspecified', team));
    } else if (groupBy === 'alpha') {
      filtered.forEach(team => {
        const first = (team.name || '?').trim().charAt(0).toUpperCase();
        push(/[A-Z]/.test(first) ? first : '#', team);
      });
    } else {
      filtered.forEach(team => push(team.country || 'Unknown', team));
    }

    Object.values(grouped).forEach(list => list.sort((a, b) => a.name.localeCompare(b.name)));
    return grouped;
  }

  const grouped = buildGroups();
  // Incomplete before Complete reads better than alphabetical for a worklist.
  const groupedEntries = groupBy === 'status'
    ? Object.entries(grouped).sort(([a]) => (a === 'Incomplete' ? -1 : 1))
    : Object.entries(grouped).sort(sortGroups);

  const leagueCount = Object.keys(leagueIndex || {}).length;

  // "Dropped out" is per league, not per club — a side knocked out of the cup
  // may still be mid-season in its domestic league, so the flag is keyed to the
  // (league, team) pair rather than stored on the team.
  const droppedPairs = new Set();
  Object.values(leagueIndex || {}).forEach(entry => {
    (entry.droppedTeamIds || []).forEach(id => droppedPairs.add(`${entry.displayName}::${id}`));
  });

  // Hiding only applies to league grouping — the checkboxes are league names.
  const visibleEntries = groupBy === 'league'
    ? groupedEntries.filter(([name]) => !hiddenLeagues.has(name))
    : groupedEntries;

  function persistHidden(next) {
    setHiddenLeagues(next);
    try {
      localStorage.setItem('teamAdmin.hiddenLeagues', JSON.stringify([...next]));
    } catch {
      /* storage unavailable — the toggles still work for this session */
    }
  }

  function toggleLeague(name) {
    const next = new Set(hiddenLeagues);
    if (next.has(name)) next.delete(name); else next.add(name);
    persistHidden(next);
  }

  const shownLeagueCount = groupedEntries.length - groupedEntries.filter(([n]) => hiddenLeagues.has(n)).length;

  const incompleteCount = teams.filter(t => !t.complete).length;
  const attentionCount = teams.filter(t => t.needsAttention).length;

  return (
    <div className="admin-layout">
      <div className="admin-sidebar">
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-title">Team configs</div>
          <input
            type="text"
            placeholder="Search teams..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              marginBottom: '8px',
              fontFamily: 'var(--font)',
              fontSize: '13px',
              padding: '6px 10px',
              border: '1px solid var(--color-card-border)',
              borderRadius: 'var(--border-radius)',
              outline: 'none',
            }}
          />
          <div className="admin-filter">
            <button className={`filter-btn ${filter === 'all' ? 'filter-btn--active' : ''}`} onClick={() => setFilter('all')}>All</button>
            <button className={`filter-btn ${filter === 'incomplete' ? 'filter-btn--active' : ''}`} onClick={() => setFilter('incomplete')}>
              Incomplete {incompleteCount > 0 && <span className="filter-badge">{incompleteCount}</span>}
            </button>
            <button className={`filter-btn ${filter === 'complete' ? 'filter-btn--active' : ''}`} onClick={() => setFilter('complete')}>Complete</button>
            <button className={`filter-btn ${filter === 'attention' ? 'filter-btn--active' : ''}`} onClick={() => setFilter('attention')}>
              ⚑ Attention {attentionCount > 0 && <span className="filter-badge">{attentionCount}</span>}
            </button>
          </div>

          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-secondary)', margin: '10px 0 4px' }}>
            Group by
          </label>
          <select
            value={groupBy}
            onChange={e => setGroupBy(e.target.value)}
            style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-card-border)', borderRadius: 'var(--border-radius)', fontSize: 13, fontFamily: 'var(--font)', boxSizing: 'border-box' }}
          >
            <option value="country">Country</option>
            <option value="league">League / competition</option>
            <option value="status">Completion status</option>
            <option value="gender">Gender</option>
            <option value="alpha">Name (A–Z)</option>
          </select>

          {groupBy === 'league' && leagueCount === 0 && (
            <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 6, fontSize: 12, background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e' }}>
              No leagues synced yet — run “Sync all leagues” to build the index.
            </div>
          )}
          {groupBy === 'league' && (
            <button
              className="btn btn--secondary"
              onClick={handleSyncAll}
              disabled={syncingAll}
              style={{ width: '100%', justifyContent: 'center', marginTop: 8, fontSize: 12 }}
            >
              {syncingAll ? 'Syncing all leagues…' : `Sync all leagues${leagueCount ? ` (${leagueCount} indexed)` : ''}`}
            </button>
          )}
          {syncAllResult && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-secondary)' }}>
              {syncAllResult.error
                ? syncAllResult.error
                : `Synced ${syncAllResult.synced.length} leagues${syncAllResult.failed.length ? `, ${syncAllResult.failed.length} failed` : ''}`}
            </div>
          )}

          {groupBy === 'league' && groupedEntries.length > 0 && (
            <div style={{ marginTop: 10, border: '1px solid var(--color-card-border)', borderRadius: 'var(--border-radius)' }}>
              <div
                onClick={() => setLeaguePanelOpen(o => !o)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-secondary)' }}
              >
                <span>Show leagues ({shownLeagueCount}/{groupedEntries.length})</span>
                <span>{leaguePanelOpen ? '▾' : '▸'}</span>
              </div>
              {leaguePanelOpen && (
                <>
                  <div style={{ display: 'flex', gap: 6, padding: '0 8px 6px' }}>
                    <button className="filter-btn" onClick={() => persistHidden(new Set())}>All</button>
                    <button className="filter-btn" onClick={() => persistHidden(new Set(groupedEntries.map(([n]) => n)))}>None</button>
                  </div>
                  <div style={{ maxHeight: 180, overflowY: 'auto', padding: '0 8px 8px' }}>
                    {groupedEntries.map(([name, teamsInGroup]) => (
                      <label
                        key={name}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 12, cursor: 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          checked={!hiddenLeagues.has(name)}
                          onChange={() => toggleLeague(name)}
                          style={{ cursor: 'pointer', flexShrink: 0 }}
                        />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>{name}</span>
                        <span style={{ opacity: 0.55, flexShrink: 0 }}>{teamsInGroup.length}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {loading ? (
          <p className="status-text" style={{ padding: '16px' }}>Loading...</p>
        ) : (
          <ul className="team-list">
            {visibleEntries.map(([groupName, groupTeams]) => (
              <React.Fragment key={groupName}>
                <li className="team-list-country">
                  {groupName}
                  <span style={{ float: 'right', opacity: 0.55, fontWeight: 400 }}>{groupTeams.length}</span>
                </li>
                {groupTeams.map(team => {
                  const dropped = groupBy === 'league' && droppedPairs.has(`${groupName}::${team.teamId}`);
                  return (
                    <li
                      /* a club can appear under several leagues, so scope the key to the group */
                      key={`${groupName}::${team.filename}`}
                      className={`team-list-item ${selected?.filename === team.filename ? 'team-list-item--active' : ''}`}
                      onClick={() => handleSelect(team)}
                      style={dropped ? { opacity: 0.6 } : undefined}
                      title={
                        team.needsAttention
                          ? `Needs attention${team.attentionNote ? `: ${team.attentionNote}` : ''}`
                          : dropped ? `${team.name} has no upcoming fixtures left in ${groupName}` : undefined
                      }
                    >
                      <span className="team-list-name">
                        {team.needsAttention && (
                          <span style={{ color: '#d97706', marginRight: 5, fontWeight: 700 }} aria-label="needs attention">⚑</span>
                        )}
                        {team.name}
                        {dropped && (
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '1px 5px', borderRadius: 8, background: '#f3f4f6', color: '#6b7280', whiteSpace: 'nowrap' }}>
                            dropped out
                          </span>
                        )}
                      </span>
                      <span className={`team-list-status ${team.complete ? 'team-list-status--complete' : 'team-list-status--incomplete'}`}>
                        {team.complete ? '✓' : `${team.missingFields.length} missing`}
                      </span>
                    </li>
                  );
                })}
              </React.Fragment>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-label">Bulk update</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn--secondary" onClick={handleBulkExport}>Export to Excel →</button>
              <button className="btn btn--primary" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                {importing ? 'Importing...' : 'Import from Excel →'}
              </button>
              <input ref={fileInputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleBulkImport} />
            </div>
          </div>
          {importResult && (
            <div className="card-body">
              {importResult.error ? (
                <p className="error-text">{importResult.error}</p>
              ) : (
                <p className="status-text">
                  <strong>{importResult.updated?.length || 0}</strong> updated &nbsp;·&nbsp;
                  <strong>{importResult.skipped?.length || 0}</strong> skipped &nbsp;·&nbsp;
                  <strong>{importResult.errors?.length || 0}</strong> errors
                </p>
              )}
            </div>
          )}
        </div>

        <div className="admin-editor">
          {!selected ? (
            <div className="admin-empty">Select a team to edit</div>
          ) : !form ? (
            <div className="admin-empty">Loading...</div>
          ) : (
            <>
              <div className="admin-form-header">
                <div className="admin-form-title">{selected.name}</div>
                <div className="admin-form-file">{selected.filename}</div>
                <LastUpdated at={form.lastUpdated} by={form.lastUpdatedBy} />
              </div>
              <div className="form-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 10px', borderRadius: 6, background: form.needsAttention ? '#fffbeb' : 'transparent', border: `1px solid ${form.needsAttention ? '#fcd34d' : 'var(--color-card-border)'}` }}>
                  <input
                    type="checkbox"
                    checked={form.needsAttention === true}
                    onChange={e => handleField('needsAttention', e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Needs attention</span>
                  <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    — flag this club as waiting on data
                  </span>
                </label>
                {form.needsAttention && (
                  <input
                    value={form.attentionNote || ''}
                    onChange={e => handleField('attentionNote', e.target.value)}
                    placeholder="What is outstanding? e.g. awaiting new stadium name from the club"
                    style={{ width: '100%', marginTop: 8, padding: '6px 10px', border: '1px solid var(--color-card-border)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--font)', boxSizing: 'border-box' }}
                  />
                )}
              </div>
              <div className="form-section">
                <div className="form-section-title">Team</div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Name</label>
                    <input value={form.name} onChange={e => handleField('name', e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>Name UEFA</label>
                    <input value={form.nameUEFA} onChange={e => handleField('nameUEFA', e.target.value)} placeholder="Leave empty if same as name" />
                  </div>
                  <div className="form-field">
                    <label>Gender <span className="required">*</span></label>
                    <select value={form.gender} onChange={e => handleField('gender', e.target.value)}>
                      <option value="">Select...</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Country <span className="required">*</span></label>
                    <input value={form.country} onChange={e => handleField('country', e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="form-section">
                <div className="form-section-title">Venue</div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Venue name <span className="required">*</span></label>
                    <input value={form.venue?.venueName || ''} onChange={e => handleField('venue.venueName', e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>Venue name UEFA</label>
                    <input value={form.venue?.venueNameUEFA || ''} onChange={e => handleField('venue.venueNameUEFA', e.target.value)} placeholder="Only if different for UEFA competitions" />
                  </div>
                  <div className="form-field">
                    <label>City <span className="required">*</span></label>
                    <input value={form.venue?.city || ''} onChange={e => handleField('venue.city', e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>State / Province</label>
                    <input value={form.venue?.stateProvince || ''} onChange={e => handleField('venue.stateProvince', e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>Country <span className="required">*</span></label>
                    <input value={form.venue?.country || ''} onChange={e => handleField('venue.country', e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="admin-form-footer">
                <button className="btn btn--danger" onClick={handleDelete}>
                  Delete config
                </button>
                {savedMsg && (
                  <span className={`save-msg ${savedMsg === 'Saved' ? 'save-msg--ok' : 'save-msg--error'}`}>{savedMsg}</span>
                )}
                <button className="btn btn--primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save config →'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================
// USERS ADMIN
// =====================
function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ username: '', full_name: '', role: 'member', email: '' });
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await fetch('/api/auth/users', { credentials: 'include' });
      const data = await res.json();
      setUsers(data.users || []);
    } catch { setMsg({ type: 'error', text: 'Failed to load users' }); }
    finally { setLoading(false); }
  }

  async function handleAdd(e) {
    e.preventDefault();
    setAdding(true);
    setMsg(null);
    try {
      const res = await fetch('/api/auth/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(addForm),
      });
      const data = await res.json();
      if (!res.ok) { setMsg({ type: 'error', text: data.error || 'Failed to create user' }); return; }
      setMsg({ type: 'success', text: `User created. Temp password: ${data.tempPassword}` });
      setShowAdd(false);
      setAddForm({ username: '', full_name: '', role: 'member', email: '' });
      load();
    } catch { setMsg({ type: 'error', text: 'Connection error' }); }
    finally { setAdding(false); }
  }

  async function handleSave(id) {
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`/api/auth/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!res.ok) { setMsg({ type: 'error', text: data.error || 'Failed to update' }); return; }
      setEditId(null);
      load();
    } catch { setMsg({ type: 'error', text: 'Connection error' }); }
    finally { setSaving(false); }
  }

  async function handleReset(id) {
    if (!window.confirm('Reset this user\'s password?')) return;
    try {
      const res = await fetch(`/api/auth/users/${id}/reset-password`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) { setMsg({ type: 'error', text: data.error || 'Failed to reset' }); return; }
      setResetResult({ userId: id, tempPassword: data.tempPassword });
    } catch { setMsg({ type: 'error', text: 'Connection error' }); }
  }

  async function handleDelete(id, username) {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/auth/users/${id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) { setMsg({ type: 'error', text: data.error || 'Failed to delete' }); return; }
      setMsg({ type: 'success', text: `User "${username}" deleted` });
      load();
    } catch { setMsg({ type: 'error', text: 'Connection error' }); }
  }

  function fmtDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString();
  }

  const ROLE_BADGE = { admin: '#7c3aed', collaborator: '#0284c7', member: '#16a34a' };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>User management</div>
        <button className="btn btn--primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => { setShowAdd(true); setMsg(null); }}>+ Add user</button>
      </div>

      {msg && (
        <div style={{ marginBottom: 12, padding: '8px 14px', borderRadius: 6, fontSize: 13, background: msg.type === 'error' ? '#fef2f2' : '#f0fdf4', color: msg.type === 'error' ? '#dc2626' : '#16a34a', border: `1px solid ${msg.type === 'error' ? '#fecaca' : '#86efac'}` }}>{msg.text}</div>
      )}

      {resetResult && (
        <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 6, fontSize: 13, background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e' }}>
          Temporary password: <strong>{resetResult.tempPassword}</strong>
          <button style={{ marginLeft: 12, fontSize: 12, cursor: 'pointer', background: 'none', border: 'none', color: '#92400e', textDecoration: 'underline' }} onClick={() => setResetResult(null)}>Dismiss</button>
        </div>
      )}

      {loading ? <p className="status-text">Loading...</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                {['Username', 'Full Name', 'Role', 'Email', 'Last Login', 'Created', 'Actions'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  {editId === u.id ? (
                    <>
                      <td style={{ padding: '6px 10px', fontWeight: 600 }}>{u.username}</td>
                      <td style={{ padding: '6px 10px' }}><input value={editForm.full_name} onChange={e => setEditForm(f => ({ ...f, full_name: e.target.value }))} style={{ width: '100%', padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: 13 }} /></td>
                      <td style={{ padding: '6px 10px' }}>
                        <select value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} style={{ padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: 13 }}>
                          <option value="admin">admin</option>
                          <option value="collaborator">collaborator</option>
                          <option value="member">member</option>
                        </select>
                      </td>
                      <td style={{ padding: '6px 10px' }}><input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} style={{ width: '100%', padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: 13 }} /></td>
                      <td style={{ padding: '6px 10px', color: 'var(--color-text-secondary)' }}>{fmtDate(u.last_login)}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--color-text-secondary)' }}>{fmtDate(u.created_at)}</td>
                      <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                        <button className="btn btn--primary" style={{ fontSize: 11, padding: '3px 8px', marginRight: 4 }} onClick={() => handleSave(u.id)} disabled={saving}>Save</button>
                        <button className="btn btn--secondary" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setEditId(null)}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '8px 10px', fontWeight: 600 }}>{u.username}</td>
                      <td style={{ padding: '8px 10px' }}>{u.full_name}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: (ROLE_BADGE[u.role] || '#666') + '22', color: ROLE_BADGE[u.role] || '#666' }}>{u.role}</span>
                      </td>
                      <td style={{ padding: '8px 10px', color: 'var(--color-text-secondary)' }}>{u.email || '—'}</td>
                      <td style={{ padding: '8px 10px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{fmtDate(u.last_login)}</td>
                      <td style={{ padding: '8px 10px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{fmtDate(u.created_at)}</td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        <button className="btn btn--secondary" style={{ fontSize: 11, padding: '3px 8px', marginRight: 4 }} onClick={() => { setEditId(u.id); setEditForm({ full_name: u.full_name, role: u.role, email: u.email || '' }); }}>Edit</button>
                        <button className="btn btn--secondary" style={{ fontSize: 11, padding: '3px 8px', marginRight: 4 }} onClick={() => handleReset(u.id)}>Reset pw</button>
                        <button className="btn btn--danger" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => handleDelete(u.id, u.username)}>Delete</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--color-surface)', borderRadius: 8, padding: 24, width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Add user</div>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--color-text-secondary)' }}>×</button>
            </div>
            <form onSubmit={handleAdd}>
              <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
                {[{ label: 'Username *', key: 'username', type: 'text', required: true }, { label: 'Full name *', key: 'full_name', type: 'text', required: true }, { label: 'Email', key: 'email', type: 'email', required: false }].map(f => (
                  <div key={f.key}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 }}>{f.label}</label>
                    <input type={f.type} required={f.required} value={addForm[f.key]} onChange={e => setAddForm(p => ({ ...p, [f.key]: e.target.value }))} style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                ))}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 }}>Role *</label>
                  <select value={addForm.role} onChange={e => setAddForm(p => ({ ...p, role: e.target.value }))} style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}>
                    <option value="member">member</option>
                    <option value="collaborator">collaborator</option>
                    <option value="admin">admin</option>
                  </select>
                </div>
              </div>
              <button type="submit" disabled={adding} className="btn btn--primary" style={{ width: '100%', justifyContent: 'center' }}>{adding ? 'Creating...' : 'Create user →'}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================
// ACTIVITY (CHANGELOG)
// =====================
const CATEGORY_LABELS = { team: 'Team', league: 'League', user: 'User', import: 'Import/Export' };
const CATEGORY_COLORS = { team: '#0284c7', league: '#7c3aed', user: '#b45309', import: '#16a34a' };

function ActivityAdmin() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => { load(category); }, [category]);

  async function load(cat) {
    setLoading(true);
    setError(null);
    try {
      const url = `/api/admin/changelog${cat ? `?category=${cat}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setEntries(data.entries || []);
    } catch { setError('Failed to load activity log'); }
    finally { setLoading(false); }
  }

  function handleExport(format) {
    window.location.href = `/api/admin/changelog/export?format=${format}${category ? `&category=${category}` : ''}`;
  }

  function fmtDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString();
  }

  function parseDetails(d) {
    if (!d) return null;
    try { return JSON.stringify(JSON.parse(d), null, 0).replace(/[{}"]/g, '').replace(/,/g, ', '); }
    catch { return d; }
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>Activity log</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="admin-filter" style={{ marginBottom: 0 }}>
            {[['', 'All'], ['team', 'Teams'], ['league', 'Leagues'], ['user', 'Users'], ['import', 'Imports']].map(([val, label]) => (
              <button key={val} className={`filter-btn ${category === val ? 'filter-btn--active' : ''}`} onClick={() => setCategory(val)}>{label}</button>
            ))}
          </div>
          <button className="btn btn--secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => handleExport('xlsx')}>Export Excel</button>
          <button className="btn btn--secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => handleExport('json')}>Export JSON</button>
        </div>
      </div>

      {error && <p className="error-text">{error}</p>}

      {loading ? <p className="status-text">Loading...</p> : entries.length === 0 ? (
        <div className="admin-empty">No activity recorded yet</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                {['Date/Time', 'User', 'Category', 'Action', 'Target', 'Details'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '7px 10px', whiteSpace: 'nowrap', color: 'var(--color-text-secondary)' }}>{fmtDate(e.logged_at)}</td>
                  <td style={{ padding: '7px 10px', fontWeight: 600 }}>{e.username}</td>
                  <td style={{ padding: '7px 10px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: (CATEGORY_COLORS[e.category] || '#666') + '22', color: CATEGORY_COLORS[e.category] || '#666' }}>
                      {CATEGORY_LABELS[e.category] || e.category}
                    </span>
                  </td>
                  <td style={{ padding: '7px 10px' }}>{e.action}</td>
                  <td style={{ padding: '7px 10px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.target}>{e.target || '—'}</td>
                  <td style={{ padding: '7px 10px', color: 'var(--color-text-secondary)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.details}>{parseDetails(e.details) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
